import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Read } from 'services/banking';
import { PerfilContent, StatusContent, Title } from 'ui/styled';
import { normalizeResponse, selectColors } from 'utils';
import { formatElapsedTime, parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function CuradoriaBanking() {
  const { user, setModal, filterExpression } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0);
  const [distribuidorId, setDistribuidorId] = useState(null);

  // Atualiza a cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 120000);
    return () => clearInterval(interval);
  }, []);

  const init = useCallback(async () => {
    setLoading(true);
    try {
      const result = await Read('?filters[status]=curadoria');
      const data = normalizeResponse(result);
      setDistribuidorId(user.distribuidor.id);
      if (!data.length) {
        setRegisters([]);
        setLoading(false);
        return;
      }
      const sorted = data.sort((a, b) => (b.id || 0) - (a.id || 0));
      const next = sorted.map((item) => ({
        ...item,
        accepted: (item.bancos || []).find((f) => f.aceito),
      }));
      setRegisters(next);
    } catch (err) {
      console.error('Erro ao carregar propostas:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [user]);
  const columns = useMemo(() => {
    const baseColumns = [
      { title: 'Id', ref: 'id_proposal', sortable: true, align: 'center' },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row?.date),
      },
      {
        title: 'SLA',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.dataSla);
          return (
            <span style={{ color: diffDays >= 2 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.dataSla)}
            </span>
          );
        },
      },
      ...(user?.administrador
        ? [
            {
              title: 'Distribuidora',
              sortable: true,
              ref: 'distributor',
            },
          ]
        : []),
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'customer' },
      {
        title: 'Perfil',
        sortable: true,
        ref: 'bureau_perfil',
        align: 'center',
        renderCell: ({ row }) => {
          const clientProfile = row?.crivo_payload?.client_profile;
          return (
            <PerfilContent color={selectColors(clientProfile)}>
              {clientProfile}
            </PerfilContent>
          );
        },
      },
      { title: 'Taxa', ref: 'taxa' },
      { title: 'Valor', ref: 'value' },
      { title: 'Entrada', ref: 'entrada' },
      { title: 'Prazo', ref: 'prazo' },
      { title: 'PMT', ref: 'valor_parcelas' },
      {
        title: '1º Vencimento',
        ref: 'data_vencimento',
        renderCell: ({ row }) =>
          formatadores.TIMESTAMP_TO_BR(row.data_vencimento),
      },
      {
        title: 'TAG',
        ref: 'status',
        renderCell: ({ row }) => {
          const proposalStatus = row?.status;

          return (
            <StatusContent
              color={selectColors(proposalStatus)}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (proposalStatus === 'Curadoria' && user?.banking) {
                  setModal({
                    type: 'curadoriabanking',
                    dados: row,
                    origem: 'negocios',
                    reload: () => init(),
                  });
                }
              }}
            >
              {proposalStatus}
            </StatusContent>
          );
        },
      },
    ];
    return baseColumns;
  }, [user, init, setModal]);

  const rows = useMemo(() => {
    return (registers || []).map((a) => ({
      id: a?.id,
      date: a?.createdAt,
      formatedDate: formatadores.TIMESTAMP_TO_BR(a?.createdAt),
      dataSla: a?.data_curadoria,
      formatedDateSla: formatadores.TIMESTAMP_TO_BR(a?.data_curadoria),
      cliente: a?.cliente,
      cod_proposta_externa: normalizeName(a?.cod_proposta_externa) || '-',
      distributor: normalizeName(a?.distribuidor?.trade_name) || '-',
      distributorId: a?.distribuidor?.id,
      integrator:
        normalizeName(a?.proposta_financeira?.integrador?.nome_fantasia) || '-',
      customer: normalizeName(a?.cliente?.nome_completo) || '-',
      value: parseCurrency(
        a?.proposta_financeira?.informacoes_projeto?.custo_total_projeto
      ),
      status: normalizeName(a?.status),
      id_proposal: a?.proposta_financeira?.id,
      proposta_financeira: a?.proposta_financeira,
      bureau_perfil: a?.bureau_perfil,
      documento_identificacao: a?.cliente?.documento_identificacao,
      comprovante_renda: a?.cliente?.comprovante_renda,
      taxa: `${a?.distribuidor?.taxas[0]?.perc_taxa}%`,
      taxas:
        a?.proposta_financeira?.propostas_banco[0]?.banco?.taxas?.find(
          (t) => t.padrao
        ) || null,
      taxas_all:
        a?.proposta_financeira?.propostas_banco[0]?.banco?.taxas || null,
      data_vencimento: !a?.proposta_financeira?.propostas_banco[0]?.data
        ? '--'
        : a?.proposta_financeira?.propostas_banco[0]?.data,
      dataVencimentoFormated: !a?.proposta_financeira?.propostas_banco[0]?.data
        ? '--'
        : formatadores.TIMESTAMP_TO_BR(
            a?.proposta_financeira?.propostas_banco[0]?.data
          ),
      valor_parcelas: parseCurrency(
        a?.proposta_financeira?.propostas_banco[0]?.valor_parcelas
      ),
      prazo: a?.proposta_financeira?.propostas_banco[0]?.parcelas,
      entrada: parseCurrency(
        a?.proposta_financeira?.propostas_banco[0]?.valor_entrada
      ),
      avalista_doc_ident: a?.avalista_doc_ident,
      avalista_comp_renda: a?.avalista_comp_renda,
      avalista_nome: a?.avalista_nome,
      avalista_cpf: a?.avalista_cpf,
      avalista_bureau_score: a?.avalista_bureau_score,
      bureau_observacoes: a?.bureau_observacoes,
      bureau_score: a?.bureau_score,
      serasa_restritivos: a?.serasa_restritivos,
      bacen_vencidos: a?.bacen_vencidos,
      bacen_prejuizo: a?.bacen_prejuizo,
      bureau_total_divida_atraso: a?.bureau_total_divida_atraso,
      bureau_divida_atraso: a?.bureau_divida_atraso,
      bureau_divida_contratada: a?.bureau_divida_contratada,
      bureau_renda_presumida: a?.bureau_renda_presumida,
      reprovado: a?.motivos_reprovado,
      parecer: a?.parecer.map((parecer) => parecer),
      crivo_payload: a?.crivo_payload,
    }));
  }, [registers]);
  useEffect(() => init(), [init, tick]);

  return (
    <ContainerAuthenticated>
      <Title>Curadoria</Title>
      <TableWrapper title="Negócios" search distribuidor={distribuidorId}>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
